import React, { ReactNode, useCallback, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { has, isEmpty } from 'lodash';
import { Stack } from '@mui/material';
import { selectCard } from 'js/actions';
import DataSetDetailActionsContainer from 'js/components/data_sets/cards/DataSetDetailActionsContainer';
import { DataSetDetailHeader } from 'js/components/data_sets/cards/DataSetDetailHeader';
import { AvailableDataSetDetailHeader } from 'js/components/data_sets_available/cards/AvailableDataSetDetailHeader';
import TagsView from 'js/components/elements/tags/TagsView';
import DataSetIcon from 'js/components/sidebar/icons/DataSetIcon';
import { RESOURCE_TYPE_DATA_SET } from 'js/constants/resourceTypes';
import { SearchFilterViews, SearchFilterViewsType } from 'js/constants/visibilityFilters';
import { isOwner, ownershipLabel, shouldDisplayOwnershipLabel } from 'js/lib/ACLUtils';
import DateUtils from 'js/lib/DateUtils';
import UIUtils from 'js/lib/UIUtils';
import { ChipWithCopy } from 'new/elements/ChipWithCopy';
import DataCard, { DataCardCell, NameDescCell } from 'new/elements/DataCard';
import { useListOldReduxContainer } from 'new/elements/DataCard/useListOldReduxContainer';
import DataCardList from 'new/elements/DataCardList';
import { mapStateToProps } from 'js/components/data_sets/cards/stateMapper';
import Typography from 'new/elements/Typography';
import FlowTypeIcon from 'js/components/pipelines/cards/FlowTypeIcon';
import PipelineStatusBadge, {
  Status,
} from 'new/components/pipelines/PipelineStatusChip/PipelineStatusChip';
import useAppDispatch from 'js/hooks/useAppDispatch';
import styles from './DataSetsTable.module.scss';
import { ConnectionTypeChip } from 'new/elements/ConnectionTypeChip/ConnectionTypeChip';
import { useResourceHeaderTabs } from 'new/hooks/useResourceHeaderTabs';
import { DataCardListProps } from 'new/elements/DataCardList/DataCardList';

type Props = {
  urlId?: string;
  data: any[];
  isLoading: boolean;
  isClientSideSearch: boolean;
  availableFilterTypes: string[];
  getSourceMeta: (id: number, isDetected?: boolean) => any;
  loadData: (
    page: any,
    currentSortCriteria: any,
    availableFilters: any,
    extraAvailableFilters: any,
    options: any,
  ) => void;
  view: SearchFilterViewsType;
  noContentView?: ReactNode;
};

const makeRowRenderer =
  ({ handleCardSelected, getData }) =>
  (index: any, item: { id: any }) => {
    const data = getData(index);
    const {
      name,
      createdAt,
      owner,
      cardData,
      readOnly,
      isManaged,
      isDetected,
      aclQueryMode,
      statsContentView,
      shared,
      parentDataSetName,
      sourceIcon,
      sourceName,
      dataSetSourceName,
    } = data;

    const readOnlyLabel = shouldDisplayOwnershipLabel(aclQueryMode)
      ? ownershipLabel(cardData)
      : null;

    const onClickCard = () => {
      if (cardData?.node_type !== 'data_retriever_holder') {
        handleCardSelected(cardData);
      }
    };

    return (
      <DataCard
        key={item.id || index}
        className={styles.cardContainer}
        onClick={onClickCard}
        disabled={cardData?.node_type === 'data_retriever_holder'}
        rowsSlots={[
          <>
            <NameDescCell
              name={name}
              className={styles.nameCardCell}
              tip={(() => {
                if (shared) return null;
                return (
                  <Typography variant="body3">
                    {isDetected ? (
                      <>
                        <b>SOURCE: </b> {dataSetSourceName}
                      </>
                    ) : (
                      <>
                        <b>FROM NEXSET: </b> {parentDataSetName}
                      </>
                    )}
                  </Typography>
                );
              })()}
            />
            ,
            <DataCardCell rightCTAsCell>
              <PipelineStatusBadge
                id={cardData.id}
                revertDirection
                status={cardData.status as Status}
                runtimeStatus={cardData?.runtime_status}
                ingestionMode={cardData.ingestion_mode}
                parentArchivalStatus={cardData?.archival_status}
              />
              {cardData.node_type !== 'data_retriever_holder' && (
                <DataSetDetailActionsContainer
                  data={cardData}
                  showStatus={false}
                  showTransform
                  showSend={!shared}
                  showMenu={!shared}
                  readOnly={readOnly}
                  isManaged={isManaged}
                  isDetected={isDetected}
                  openDetailsModal={onClickCard}
                />
              )}
            </DataCardCell>
          </>,
          <DataCardCell className={styles.dataSetCardCell}>
            <Stack direction="row" justifyContent="center" alignItems="center" gap="16px">
              <Stack direction="row" gap="8px" alignItems="center">
                <DataSetIcon className={styles.dataSetIcon} />
                {!shared ? (
                  <>
                    <span className="data-set-text">from</span>
                    {isDetected ? (
                      <Stack className={styles.resourceInfo}>
                        <ConnectionTypeChip
                          icon={sourceIcon}
                          label={sourceName}
                          style={{ border: 'none', padding: 0 }}
                        />
                      </Stack>
                    ) : (
                      <DataSetIcon className={styles.dataSetIcon} />
                    )}
                  </>
                ) : null}
              </Stack>
              <ChipWithCopy tooltipTitle="Copy Nexset ID" toCopy={cardData.id}>
                ID {cardData.id}
              </ChipWithCopy>
              <FlowTypeIcon data={cardData} className={styles.flowTypeContainer} />
            </Stack>
          </DataCardCell>,
          <DataCardCell multiCol className={styles.dataSetCardCell}>
            {!shared && <Typography variant="body3">Created on {createdAt}</Typography>}
            {shared && !readOnlyLabel && (
              <Typography variant="body3">Shared by {owner.email}</Typography>
            )}
            {readOnlyLabel && (
              <>
                <Typography variant="caption">{readOnlyLabel}</Typography>
                {!isOwner(cardData) && (
                  <Typography variant="body3">Owned by {owner.email}</Typography>
                )}
              </>
            )}
            ,{statsContentView}
          </DataCardCell>,
          !isEmpty(cardData.tags) && (
            <DataCardCell className={styles.dataSetCardCell}>
              <TagsView data={cardData} resourceType={RESOURCE_TYPE_DATA_SET} />
            </DataCardCell>
          ),
        ].filter(Boolean)}
      />
    );
  };

const DataSetsTable = ({
  availableFilterTypes,
  view,
  urlId,
  isLoading,
  isClientSideSearch,
  data,
  getSourceMeta,
  loadData,
  noContentView,
}: Props) => {
  const dispatch = useAppDispatch();
  const publicDatasets = useSelector((state: any) => state.publicDatasets);
  const sharedDatasets = useSelector((state: any) => state.sharedDatasets);
  const { selectedCardId } = useSelector((state: any) => state.app.listing);

  const selectedDataSet = data?.find((set) => set.id === selectedCardId);
  const detailViewTabs = (useResourceHeaderTabs(selectedDataSet, {
    resourceType: RESOURCE_TYPE_DATA_SET,
    flow: null,
  }) || []) as any[];

  const handleCardSelected = useCallback((row: { id: any }) => {
    dispatch(selectCard(row.id));
  }, []);

  useEffect(() => {
    if (urlId) {
      handleCardSelected({ id: urlId });
    }
  }, []);

  const getData = useListOldReduxContainer(data, mapStateToProps, (ownProps) => {
    const {
      cardData,
      readOnly,
      adminAccess,
      aclQueryMode,
      isManaged,
      owner,
      summaryData,
      isDetected,
      parentDataSet,
      source,
      connectionOrVendorType,
    } = ownProps;
    if (!cardData) return null;
    const createdAt = DateUtils.convertDateStringToFormat(cardData.created_at);
    const name = cardData.name || '';
    const description = cardData.description || ' ';
    const status = cardData.status;
    const shared =
      publicDatasets.byId.includes(cardData.id) || sharedDatasets.byId.includes(cardData.id);

    let parentDataSetName = null,
      sourceIcon = null,
      sourceName = null,
      dataSetSourceName = null;

    if (isDetected === true) {
      const connectionInfo = getSourceMeta(cardData.id, isDetected);
      dataSetSourceName = source ? source.name : null;

      if (connectionInfo) {
        sourceIcon = connectionInfo.iconSm;
        sourceName = connectionInfo.name;
      } else {
        sourceName = connectionOrVendorType ? connectionOrVendorType : '';
      }
    } else {
      parentDataSetName = parentDataSet ? parentDataSet.name : '';
    }

    let statsContentView = null;
    if (summaryData) {
      let derivedDataSetsCount = has(summaryData, 'data_sets.total')
        ? summaryData.data_sets.total
        : 0;
      let derivedDataSetsLabel = UIUtils.toPluralFormat(
        'Transformed Nexset',
        derivedDataSetsCount,
        false,
      );
      let sinksCount = has(cardData, 'data_sinks') ? cardData.data_sinks.length : 0;
      let sinksLabel = UIUtils.toPluralFormat('Destination', sinksCount, false);
      let sharersCount = UIUtils.getDatasetSharersCount(cardData, summaryData);
      let sharersLabel = UIUtils.toPluralFormat('Share', sharersCount, false);
      if (!shared) {
        statsContentView = (
          <div className="counter-holder">
            <span className="counter">{derivedDataSetsCount} </span>
            <span className="counter-label">{derivedDataSetsLabel}</span>
            <span className="counter-label">{', '}</span>
            <span className="counter">{sinksCount} </span>
            <span className="counter-label">{sinksLabel}</span>
            <span className="counter-label">{', '}</span>
            <span className="counter">{sharersCount} </span>
            <span className="counter-label">{sharersLabel}</span>
          </div>
        );
      }
    }

    return {
      cardData,
      ...cardData,
      name,
      description,
      createdAt,
      owner,
      readOnly,
      adminAccess,
      isManaged,
      aclQueryMode,
      shared,

      isDetected,

      status,
      statsContentView,

      parentDataSetName,
      sourceIcon,
      sourceName,
      dataSetSourceName,
    };
  });

  const isAvailableDataSetsView = view === SearchFilterViews.AVAILABLE_DATA_SETS;
  const ViewHeader = isAvailableDataSetsView ? AvailableDataSetDetailHeader : DataSetDetailHeader;

  return (
    <DataCardList
      data={data}
      view={view}
      availableFilterTypes={availableFilterTypes}
      detailViewTabs={detailViewTabs}
      detailHeaderContent={
        <ViewHeader {...getData(data.findIndex((item: any) => item.id === selectedCardId))} />
      }
      urlId={urlId}
      loading={isLoading}
      isClientSideSearch={isClientSideSearch}
      enableSorting={!isAvailableDataSetsView}
      enablePagination={!isAvailableDataSetsView}
      rowRenderer={makeRowRenderer({
        handleCardSelected,
        getData,
      })}
      getSourceMeta={getSourceMeta}
      noContentView={noContentView}
      loadData={loadData as DataCardListProps['loadData']}
    />
  );
};

export default DataSetsTable;
